import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';

import { HttpErrorResponse } from "@angular/common/http";
import { Subject, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { JSR310ConversionService } from '../../../../shared/jsr310-localdates/services/jsr310-conversion.service';

import { AccesService } from '../services/acces.service';
import { OuvrageAccesDTO } from '../entities/ouvrage-acces-dto.entity';

import { ArretSlbService } from '../../arret-slb/services/arret-slb.service';
import { ArretSlbTLR } from '../../arret-slb/entities/arret-slb-tlr.entity';

import { UserService } from '../../../humain/user/services/user.service';
import { AccesParcInfo } from '../entities/acces-parc-info.entity';

type DisplayedArretSlb = {
	entity: ArretSlbTLR,
	start: Date | null,
	end: Date | null,
	actualized: boolean,
}

type RadioOption<T> = {
	value: T,
	label: string,
}

export enum TypeArret{
	KOA = 0,
	KOM = 1,
}

@Component({
	selector: 'acces-association-arrets',
	templateUrl: 'acces-association-arrets.html',
	styleUrls: ['acces-association-arrets.css'],
})
export class AccesAssociationArretsComponent {
	// Gestion globale de la fenetre
	choixDureeSubject = new Subject<number>();
	arretsOuvrageLoading = false;
	arretsOuvrageLoadingError: string | null = null;
	commentaireArretsDisabled = false;
	hasNonActualizedArretsAcces = false;
	arretsOuvrageSelectedMap = new Map<string, boolean>();
	arretsOuvrageAssociatedMap = new Map<string, AccesParcInfo>();

	// Donnees a lier sur la vue
	dureeOptions: RadioOption<number>[] = [
		{
			value: 1,
			label: 'J - 1',
		},
		{
			value: 2,
			label: 'J - 2',
		},
		{
			value: 3,
			label: 'J - 3',
		}
	];
	
	ouvrages!: OuvrageAccesDTO[];
	arretsOuvrage!: DisplayedArretSlb[];

	arretsAcces!: DisplayedArretSlb[];
	
	// Saisies de l'utilisateur
	choixDuree!: number;
	arretsOuvrageSelected!: boolean[];
	commentaireArrets!: string;
	signalerNoArretsCheckbox!: boolean | false;
	typeArretOptions = [TypeArret.KOA, TypeArret.KOM];
	
	private typeArretLabels: string[] = ["KOA", "KOM"];
	getTypeArretLabel = (typeArret: TypeArret) => this.typeArretLabels[typeArret];
	
	typeArretShouldBeSelectedByDefaut = (_typeArret: TypeArret) => true;

	typeArretSelected: TypeArret[] = this.typeArretOptions; 

	constructor(
		@Inject(JSR310ConversionService) conversionService: JSR310ConversionService,
		
		@Inject(AccesService) accesService: AccesService,
		@Inject(ArretSlbService) arretSlbService: ArretSlbService,
		@Inject(UserService) userService: UserService,
	) {
		// Fonction qui cree des DisplayedArret a partir d'un liste de ArretSlbTLR par conversion des dates au format JS
		let buildDisplayedArret = (arret: ArretSlbTLR, actualized: boolean) => ({
				entity: arret,
				start: conversionService.fromNullableLocalDateTime(arret.startDate),
				end: conversionService.fromNullableLocalDateTime(arret.endDate),
				actualized: actualized,
			} as DisplayedArretSlb)
		;
		let buildDisplayedArretList = (arrets: ArretSlbTLR[], actualized: boolean) => arrets.map(
			(arret) => buildDisplayedArret(arret, actualized)
		);

		// Des le chargement du composant 
		// 1- Charger l'utilisateur courrant
		let user$ = userService.getCurrentUserTLR();

		// A la reception d'une demande d'affichage
		// Charger l'acces correspondant
		let acces$ = this.displayCommandsSubject.pipe(
			mergeMap(
				(accesId) => accesService.getById(accesId)
			)
		);

		// Une fois l'acces obtenu, charger toutes les donnees complementaires
		let accesData$ = acces$.pipe(
			mergeMap(
				(acces) => {
					let ouvrages$ = accesService.getOuvrageDTOByAcces(acces.id);
				
					let arretsAcces$ = arretSlbService.getByAcces(acces.id).pipe(
						// Transformer les timestamps en dates
						map((arrets) => buildDisplayedArretList(arrets, false))
					);

					return forkJoin({
						acces: of(acces), 
						ouvrages: ouvrages$,
						arretsAcces: arretsAcces$,
					});
				}
			)
		);

		// Uniquement quand tous les elements sont charges, on bind les donnees sur les vue
		combineLatest([user$, accesData$]).subscribe(
			(data) => {
				let accesData = data[1];
				let acces = accesData.acces;
				this.ouvrages = accesData.ouvrages.sort((a, b) => a.nom.localeCompare(b.nom));
				this.arretsAcces = accesData.arretsAcces;
				
				this.arretsAcces.forEach(arret => {
				    this.arretsOuvrageSelectedMap.set(arret.entity.uid, true);
				});

				this.signalerNoArretsCheckbox =  accesData.acces.signalerAbsenceArrets;

				// Initialiser les champs de saisie
				let now = new Date();
				// Arondir aux 5 minutes superieures
				let minutes = now.getMinutes();
				if ((minutes % 5) > 0) {
					let missing = 5 - (minutes % 5);
					now.setMinutes(minutes + missing); 
				}
				this.choixDuree = 1;
				this.commentaireArrets = acces.etat === 2 ? '' : acces.commentaireArrets;
				this.commentaireArretsChange.emit(this.commentaireArrets);
				
				// NB: attendre volontairement la premiere evaluation du commentaire avant
				// de mettre a jour les dependances des arrets de l'acces car elles peuvent effacer le dit commentaire a raison
				this.updateArretsDependencies();

				this.arretsOuvrage = [];
				this.arretsOuvrageLoading = true;
				this.arretsOuvrageLoadingError = null;

				// Si jamais on a mis une valeur dans arretMachine, il faut declencher le chargement des stops
				this.choixDureeSubject.next(this.choixDuree);
				
				// Si on a des arrets dejas lies a l'acces, il faut tenter l'actualisation de leurs donnees par Darwin
				// On le fait dans un deuxieme temps, apres la mise en place des bindings et non dans le pipe, car ces
				// actualisations de donnees doivent etre non-bloquantes pour la page en cas de probleme
				let updateActualizationMessage = () => {
					this.hasNonActualizedArretsAcces = this.arretsAcces.findIndex($ => !$.actualized) > -1;
				}
				updateActualizationMessage();
				for (let arretAcces of this.arretsAcces) {
					arretSlbService.getActualizationByUid(arretAcces.entity.uid).subscribe(
						(actualisation) => {
							arretAcces.entity = actualisation;
							arretAcces.start = conversionService.fromNullableLocalDateTime(actualisation.startDate);
							arretAcces.end = conversionService.fromNullableLocalDateTime(actualisation.endDate),
							arretAcces.actualized = true;
							updateActualizationMessage();
						}
					);
				}
			}
		)
		
		// A la reception d'un changement de la duree de recherche des arrets par un utilisateur
		this.choixDureeSubject.subscribe(
			// NB: on incorpore la logique dans le subscribe, car l'appel au serveur peut echouer, mais on veut que ce soit non bloquant
			// Comme une erreur dans un observable bloque definitivement le fonctionnement de l'observable,
			// il faut que l'appel soit sur un observable independant, recree a chaque changement de la duree
			// Ainsi en cas d'erreur, l'observable choixDureeSubject continuera a fonctionner
			(duree) => {
				this.arretsOuvrageLoading = true;
				this.arretsOuvrageLoadingError = null;

				// Appel du serveur pour recuperer les arrets
				let arretsOuvrage$ = this.ouvrages ? arretSlbService.getByOuvrage(this._accesId, this.ouvrages[0].id, duree) : of([])
				arretsOuvrage$.pipe(
					// En cas d'erreur, extraire le message d'erreur et considerer qu'on a trouve aucun arret
					catchError(
						(error: HttpErrorResponse) => {
							this.arretsOuvrageLoadingError = "Les arrêts n'ont pas pu être récupérés";
							console.error("Erreur lors de la récupération des arrêts", error);
							return of([] as ArretSlbTLR[]);
						}
					),
					// Transformer les timestamps en dates
					map((arrets) => buildDisplayedArretList(arrets, true))
				)
				.subscribe(
					(arretsOuvrage) => {
						this.arretsOuvrageLoading = false;
						this.arretsOuvrage = arretsOuvrage.sort((a, b) => {
							if (a.start == null)
							 	return b.start == null ? 0 : 1;
							if (b.start == null)
								return -1;
							
							return b.start.getTime() - a.start.getTime();	
						});
						
						// Evaluer les stops actuellement selectionnes (en tant qu'ArretSlb)
						let isPresentAsArretSlb = (uid: string) => this.arretsAcces.findIndex($ => $.entity.uid === uid) > -1;
						this.arretsOuvrageSelected =  this.arretsOuvrage.map($ => isPresentAsArretSlb($.entity.uid));
						
						// Vérifier quels arrêts sont déjà associés à un accès
						let uids = this.arretsOuvrage.map($ => $.entity.uid);

						this.arretsOuvrageAssociatedMap.clear();

						if (uids.length > 0) {
							arretSlbService.adviseIfStopsAreAssociatedToAcces(uids).subscribe(
								(associations) => {
									Object.keys(associations).forEach((uid) => {
										this.arretsOuvrageAssociatedMap.set(uid, associations[uid]);
									});
								}
							);
						}
					}
				);
			}
		);
	}

	// Liaison de l'observable servant a commander l'affichage du panneau
	private displayCommandsSubject = new Subject<number>();
	private _accesId !: number;
	@Input() set accesId(value: number | undefined) {
		if (value !== undefined) {
			this.displayCommandsSubject.next(value);
			this._accesId = value;
		}
	}
	
	@Input() initialArretsAcces: ArretSlbTLR[] = [];
	
	@Output() commentaireArretsChange = new EventEmitter<string>();
	@Output() arretsAccesChange = new EventEmitter<ArretSlbTLR[]>();
	@Output() onSignalementNoArrets = new EventEmitter<boolean>();

	private updateArretsDependencies() {
		this.arretsAccesChange.emit(this.arretsAcces.map($ => $.entity));
		
		let status = this.arretsAcces && this.arretsAcces.length > 0;
		this.commentaireArretsDisabled = status;
		if (status) {
			this.commentaireArrets = "";
			this.commentaireArretsChange.emit("");
		}
	}	
	
	addArret(arret: DisplayedArretSlb) {
		let uid = arret.entity.uid;
		this.arretsOuvrageSelectedMap.set(uid, true);
		this.arretsAcces.push(arret);
		this.updateArretsDependencies();
		
		if (this.arretsAcces.length > 0){
			this.signalerNoArretsCheckbox = false;
			this.onSignalementNoArrets.emit(false);
		}
	}
	
	removeArret(index: number) {
		let displayedArret = this.arretsAcces[index];
		let uid = displayedArret.entity.uid;

		if (this.arretsOuvrageSelectedMap.has(uid)) {
			this.arretsOuvrageSelectedMap.set(uid, false);
		}
		
		this.arretsAcces.splice(index, 1);
		this.updateArretsDependencies();
	}

	signalerNoArrets(selected: boolean){
		this.onSignalementNoArrets.emit(selected);
	}
	
	onTypeArretSelectionChanged([selectedTypes, allChecked]: [TypeArret[], boolean]) {
	    this.typeArretSelected = selectedTypes;
	}
	
	keepOnlyChosenTypes(displayedArret: DisplayedArretSlb, typeArretSelected: TypeArret[]) {
		let statusCode: TypeArret = displayedArret.entity.statusCode === 'KOA' ? TypeArret.KOA : TypeArret.KOM;
		return typeArretSelected.includes(statusCode);
	}
	
	getAssociationTooltip(uid: string): string {
		let info = this.arretsOuvrageAssociatedMap.get(uid);
		return info ? `Déjà associé à l'accès ${info.numero} sur le parc ${info.parcCode}` : '';
	}
}